
import { useState, useEffect } from 'react';
import { toast } from "sonner";
import { processPayment } from '@/utils/paymentUtils';
import { appConfig } from '@/config/appConfig';

interface PaymentProcessingProps {
  selectedOffer: 'standard' | 'premium' | 'exclusive';
  offerPrice: number;
  onSubmitSuccess: () => void;
  numberOfRooms?: number;
  roomAreas?: number[];
  totalSquareArea?: number;
}

interface OrderDetails {
  customerName: string;
  offerType: 'standard' | 'premium' | 'exclusive';
  offerPrice: number;
  initialPayment: number;
  remainingPayment: number;
  email: string;
  phone: string;
  address: string;
  numberOfRooms?: number;
  roomAreas?: number[];
  totalSquareArea?: number;
}

export const usePaymentProcessing = ({ 
  selectedOffer, 
  offerPrice,
  onSubmitSuccess,
  numberOfRooms,
  roomAreas,
  totalSquareArea
}: PaymentProcessingProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingStep, setProcessingStep] = useState<number>(0);
  const [progressValue, setProgressValue] = useState<number>(0);
  const [formDataToProcess, setFormDataToProcess] = useState<any>(null);
  
  // Log the received values
  console.log('usePaymentProcessing - received values:', { 
    numberOfRooms, 
    roomAreas, 
    totalSquareArea 
  });
  
  const processingSteps = [
    "Przygotowywanie zamówienia...",
    "Weryfikacja danych...",
    "Generowanie i wysyłanie umowy...",
    "Generowanie i wysyłanie faktury...",
    "Przygotowywanie płatności...",
    "Finalizacja zamówienia..."
  ];
  
  useEffect(() => {
    if (!isSubmitting || processingStep >= processingSteps.length) return;
    
    const timer = setTimeout(() => {
      advanceProcessingStep();
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [isSubmitting, processingStep]);
  
  useEffect(() => {
    if (isSubmitting && processingStep === processingSteps.length && formDataToProcess) {
      executePaymentProcessing(formDataToProcess);
    }
  }, [processingStep, isSubmitting, formDataToProcess]);
  
  const advanceProcessingStep = () => {
    const newStep = processingStep + 1;
    setProcessingStep(newStep);
    setProgressValue((newStep / processingSteps.length) * 100);
  };
  
  const startSubmitProcess = (formData: any) => {
    setIsSubmitting(true);
    setProcessingStep(0);
    setProgressValue(0);
    setFormDataToProcess(formData);
  };
  
  const createOrderDetails = (formData: any): OrderDetails => {
    console.log('createOrderDetails - using values:', {
      numberOfRooms,
      roomAreas,
      totalSquareArea
    });
    
    return {
      customerName: `${formData.firstName} ${formData.lastName}`,
      offerType: selectedOffer,
      offerPrice: offerPrice,
      initialPayment: offerPrice * 0.7,
      remainingPayment: offerPrice * 0.3,
      email: formData.email,
      phone: formData.phone || "Not provided",
      address: formatAddress(formData),
      numberOfRooms: numberOfRooms,
      roomAreas: roomAreas,
      totalSquareArea: totalSquareArea
    };
  };
  
  const formatAddress = (formData: any): string => {
    return `${formData.street} ${formData.buildingNumber}${formData.apartmentNumber ? '/' + formData.apartmentNumber : ''}, ${formData.postalCode} ${formData.city}`;
  };
  
  const createPaymentData = (formData: any) => {
    // Ensure offerPrice is converted to string
    const stringAmount = String(offerPrice);
    
    console.log('createPaymentData - using values:', { 
      numberOfRooms, 
      roomAreas, 
      totalSquareArea 
    });
    
    return {
      recipientName: `${formData.firstName} ${formData.lastName}`,
      recipientAddress: formatAddress(formData),
      recipientAccountNumber: "PL59105014611000009106301634",
      transferTitle: `Zamówienie klimatyzacji - pakiet ${selectedOffer === 'standard' ? 'Standard' : 'Premium'}`,
      amount: stringAmount,
      userId: formData.email,
      phone: formData.phone,
      callbackSuccess: window.location.origin + "/payment-success",
      callbackFailure: "https://fizen.com/error",
      linkExpirationDays: 1,
      numberOfRooms: numberOfRooms, // Include number of rooms
      roomAreas: roomAreas, // Include room areas
      totalSquareArea: totalSquareArea // Include total square area
    };
  };
  
  const handlePaymentRedirect = (paymentUrl: string) => {
    if (appConfig.payment.useMockPayment) {
      setTimeout(() => {
        window.location.href = paymentUrl;
      }, appConfig.payment.mockRedirectDelay);
    } else {
      setTimeout(() => {
        window.location.href = paymentUrl;
      }, 1500);
    }
  };
  
  const executePaymentProcessing = async (formData: any) => {
    try {
      const paymentData = createPaymentData(formData);
      
      const orderDetails = createOrderDetails(formData);
      sessionStorage.setItem('orderDetails', JSON.stringify(orderDetails));
      
      console.log("Procesowanie zamówienia z danymi:", paymentData);
      console.log("Dodatkowe dane zamówienia:", orderDetails);
      
      const loadingToast = toast.loading("Przekierowywanie do płatności", {
        description: appConfig.payment.useMockPayment ? 
          "Tryb testowy - bez faktycznej płatności." : 
          "Prosimy o chwilę cierpliwości."
      });
      
      try {
        const paymentUrl = await processPayment(paymentData);
        
        console.log("Otrzymano URL płatności:", paymentUrl);
        
        toast.dismiss(loadingToast);
        
        toast.success("Zamówienie przyjęte", {
          description: appConfig.payment.useMockPayment ? 
            "Tryb testowy - za moment nastąpi przekierowanie." : 
            "Za moment nastąpi przekierowanie do systemu płatności."
        });
        
        handlePaymentRedirect(paymentUrl);
      } catch (error) {
        handlePaymentError(error, loadingToast);
      }
    } catch (error) {
      console.error("Błąd podczas przygotowania danych płatności:", error);
      toast.error("Błąd podczas przetwarzania zamówienia", {
        description: "Wystąpił nieoczekiwany problem. Prosimy spróbować ponownie później.",
      });
      setIsSubmitting(false);
    }
  };
  
  const handlePaymentError = (error: any, loadingToast: string | number) => {
    toast.dismiss(loadingToast);
    console.error("Błąd podczas przetwarzania zamówienia:", error);
    toast.error("Błąd podczas przetwarzania zamówienia", {
      description: "Wystąpił problem z systemem płatności. Prosimy spróbować ponownie lub skontaktować się z obsługą.",
    });
    setIsSubmitting(false);
  };
  
  return {
    isSubmitting,
    processingStep,
    progressValue,
    processingSteps,
    startSubmitProcess,
    processPaymentData: startSubmitProcess
  };
};
